import {firstValueFrom} from 'rxjs';
import {Injectable, signal} from '@angular/core';
import {DashboardStudyLoadFilesService} from '../dashboard-study-load-files/dashboard-study-load-files.service';
import {DashboardStudyViewerMode} from '../../symbols';
import {
  DashboardStudyPapayaControllerService,
  PapayaScanController,
  PapayaSurfaceController,
} from '../../../dashboard-study-papaya/services/dashboard-study-papaya-controller/dashboard-study-papaya-controller.service';
import {DashboardSeriesDerivativeType, DashboardSeriesScanClass} from '../../../dashboard-new/types/dashboard.types';
import {computeDiffs} from '../../../shared/utils/compute-diffs.utils';
import {OriginalPapayaSurfaces} from '../../../dashboard-study-papaya/types/dashboard-study-papaya-original.types';
import {
  hexColorToPapayaRGBColor,
  hexColorToRGBColor,
} from '../../../dashboard-papaya/symbols/dashboard-papaya-general.symbols';
import {RGBAColor} from '../../../dashboard-papaya/symbols/dashboard-papaya-color-table.symbols';
import {
  DashboardViewRegion,
  DashboardViewRegionBodySegment,
  DashboardViewRegionMriLabel,
  DashboardViewRegionPosition,
  DashboardViewRegionZoom3D,
} from '../../../dashboard-view-regions/types/dashboard-view-regions.types';
import {PapayaSurfaceControllerIdRelation} from '../../../dashboard-study-papaya/types/dashboard-study-papaya.types';
import {DashboardStudyViewerLoadingState} from '../../symbols/dashboard-study-viewer.symbols';

/**
 * Interface representing the required controls for the dashboard view region.
 */
interface DashboardViewRegionRequiredControls {
  /** Scan controls needed for the view region. */
  scanControls: PapayaScanController[];

  /** Surface controls needed for the view region. */
  surfaceControls: PapayaSurfaceController[];
}

@Injectable()
export class DashboardStudyViewerControllerService {
  /** Default 3D point in the Papaya viewer for the view region. */
  public readonly defaultViewRegion3DPoint: DashboardViewRegionPosition = [128, 128, 128];

  /** Default inspect point in the Papaya viewer for the view region. */
  public readonly defaultViewInspectPoint: DashboardViewRegionPosition = [128, 128, 128];

  /** Default zoom level for the 3D view region in the Papaya viewer. */
  public readonly defaultViewRegionZoom3D: DashboardViewRegionZoom3D = 300;

  /** Current loading status of the Papaya viewer. */
  public currentLoadingStatus = signal<DashboardStudyViewerLoadingState>(DashboardStudyViewerLoadingState.notInitialized);

  /**
   * Indicates whether the Papaya viewer has been rendered (instance created and displayed on the screen).
   */
  public isPapayaRendered = signal<boolean>(false);

  /**
   * The current viewer mode of the study.
   * Defines what images are shown in the viewer.
   */
  public studyViewerMode = signal<DashboardStudyViewerMode>(null);

  /**
   * For the scan view we always show one label image above one base image.
   * Both images can be any scan class.
   * The label image scan class is defined by the view region.
   * The base image scan class is defined by the user (they may select any scan class).
   */
  public selectedBaseScanClass = signal<DashboardSeriesScanClass>(null);

  /**
   * The view region that is currently rendered in the Papaya viewer.
   */
  public renderedViewRegion = signal<DashboardViewRegion>(null);

  /**
   * Indicates whether the Papaya viewer is in orthogonal view mode.
   * A bit different from the study viewer mode as it represents the current Papaya state at the moment of time.
   * While the study viewer mode represents the user-selected mode, and it can be in process of changing.
   */
  public isPapayaOrthogonalView = signal<boolean>(false);

  /**
   * Indicates whether the study viewer is in zoomed view mode.
   */
  public isZoomedView = signal<boolean>(false);

  /**
   * The list of scan controls that are currently in view.
   */
  public scanControlInViewIds = new Set<PapayaScanController['id']>();

  /**
   * The list of surface controls that are currently in view.
   */
  public surfaceControlInViewIds = new Set<PapayaSurfaceController['id']>();

  constructor(private dashboardStudyLoadFilesService: DashboardStudyLoadFilesService, public papayaController: DashboardStudyPapayaControllerService) {}

  /**
   * Focused the provided view region in the Papaya viewer.
   * Updates the related statuses and controls based on the view region.
   */
  public async focusViewRegion(viewRegion: DashboardViewRegion, redraw = true): Promise<void> {
    /**
     * Indicates whether the scan view was loaded at least once.
     * Used to determine if we need to move the scan inspect point when the same view region is focused again.
     * We have to move it if the view region was changed, or if the scan view was not rendered yet.
     */
    let hasCachedScanView = false;

    // If papaya is not yet rendered, we need to initialize it.
    if (!this.isPapayaRendered()) {
      // Initialize the Papaya controller with the view region.
      await this.initialViewRegionLoad(viewRegion);
    } else {
      this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.calculatingChanges);
      const previousBodySegment = this.renderedViewRegion()?.body_segment;

      // If the body segment of the current view region is different from the one in the viewer, we need to update the base scan class.
      if (previousBodySegment !== viewRegion.body_segment) {
        const initialBaseScanClass = defineInitialBaseScanClass(viewRegion);
        this.selectedBaseScanClass.set(initialBaseScanClass);
      }

      // If the body segment is FullBody and the viewer mode is SurfaceWithProfile, switch to Surface mode.
      // This is because the profile view does not make sense for the full body.
      if (
        viewRegion.body_segment === DashboardViewRegionBodySegment.FullBody &&
        this.studyViewerMode() === DashboardStudyViewerMode.SurfaceWithProfile
      ) {
        this.studyViewerMode.set(DashboardStudyViewerMode.Surface);
      }

      // Update the papaya surface view mode based on the current viewer mode.
      const viewerMode = this.studyViewerMode();
      this.papayaController.currentPapayaInstance.viewer.isSurfaceViewMode =
        viewerMode === DashboardStudyViewerMode.Surface || viewerMode === DashboardStudyViewerMode.SurfaceWithProfile;

      this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.loadingFiles);
      // Get the necessary controls for the current mode.
      const controlsNeeded = await this.getNecessaryControlsForCurrentMode(viewRegion);

      // Get the scan controls that are currently in view.
      const scanControlInViewIds = Array.from(this.scanControlInViewIds);

      // Mark the scan view as cached if there are any scan controls in view.
      hasCachedScanView = scanControlInViewIds.length > 0;

      // Do not update the controls if no controls are needed for the current view.
      // This way we can avoid unnecessary reloading of the controls for cases when user switches between the view modes.
      if (controlsNeeded.scanControls.length > 0) {
        this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.updatingScanControls);
        const controlsNeededIds = controlsNeeded.scanControls.map(scanControl => scanControl.id);

        const {firstToSecond: scansToUnload, secondToFirst: scansToRender} = computeDiffs(scanControlInViewIds, controlsNeededIds);

        // Remove the scans that are not needed anymore from the Papaya viewer.
        for (const scanControlId of scansToUnload) {
          this.papayaController
            .scanControllerList()
            .find(id => id.id === scanControlId)
            .removeSliceFromPapayaViewer(false);
          this.scanControlInViewIds.delete(scanControlId);
        }

        // Load the scans that are needed for the current view to the Papaya viewer.
        for (const scanControlId of scansToRender) {
          await this.papayaController
            .scanControllerList()
            .find(id => id.id === scanControlId)
            .loadImageToPapaya();
          this.scanControlInViewIds.add(scanControlId);
        }

        // Reorder the scan controllers by priority after loading the new scans.
        // It's necessary to ensure that the base scans are placed below the labeled scans in the viewer.
        // Otherwise, the labeled scans won't be visible.
        // By default, they are loaded in the correct order, but if the user selects a different scan class,
        // it will be placed above the labeled scans.
        // To avoid this, we trigger the custom sort of the Papaya slices when the body segment was not changed.
        if (previousBodySegment === viewRegion.body_segment) {
          this.papayaController.customSortPapayaSlices();
        }
      }

      // Do not update the controls if no controls are needed for the current view.
      // This way we can avoid unnecessary reloading of the controls for cases when user switches between the view modes.
      if (controlsNeeded.surfaceControls.length > 0) {
        this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.updatingSurfaceControls);
        const controlsNeededIds = controlsNeeded.surfaceControls.map(surfaceControl => surfaceControl.id);
        const surfaceControlInViewIds = Array.from(this.surfaceControlInViewIds);
        const {firstToSecond: surfacesToUnload, secondToFirst: surfacesToRender} = computeDiffs(surfaceControlInViewIds, controlsNeededIds);

        // Load the surfaces to the Papaya viewer.
        await this.papayaController.loadSurfaceControlsToPapaya(surfacesToRender);

        // Store the surface controls that are currently in view.
        for (const surfaceControlName of surfacesToRender) {
          this.surfaceControlInViewIds.add(surfaceControlName);
        }

        // Unload the surfaces that are not needed anymore.
        for (const surfaceControlName of surfacesToUnload) {
          const surfaceControllerIdx = this.papayaController.surfaceIdToIndexMap.get(surfaceControlName);

          this.papayaController.surfaceControllerList()[surfaceControllerIdx].removeSurfaceFromPapayaViewer(false);
          this.surfaceControlInViewIds.delete(surfaceControlName);
        }
      }
    }
    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.updatingViewPerspective);

    // Set the coordinate system sizes based on the body segment of the view region.
    // For the full body, we have the following sizes: x: 257, y: 111, z: 1015.
    // For the brain, we have the following sizes: x: 256, y: 256, z: 256.
    // Probably, we will have different sizes in the future; move them to the View Region model when needed.
    if (viewRegion.body_segment === DashboardViewRegionBodySegment.FullBody) {
      this.papayaController.setSurfaceViewImageDimensions({x: 257, y: 111, z: 1015});
      this.papayaController.setSurfaceViewVoxelDimensions({x: 1.75, y: 3.5, z: 1.75});
      this.papayaController.setSurfaceViewOrigin({x: 128, y: 64, z: 77});
    } else {
      this.papayaController.setSurfaceViewImageDimensions({x: 256, y: 256, z: 256});
      this.papayaController.setSurfaceViewVoxelDimensions({x: 1, y: 1, z: 1});
      this.papayaController.setSurfaceViewOrigin({x: 128, y: 128, z: 128});
    }
    this.papayaController.updateSurfaceVolumeSizes();

    // Set the rendered view region to the new view region.
    this.rearrangeSlicesToMatchViewerMode(this.studyViewerMode());

    // Update the surface color and opacity based on the view region.
    const renderedSurfaceControllerIds = Array.from(this.surfaceControlInViewIds);
    if (
      renderedSurfaceControllerIds.length > 0 &&
      (this.studyViewerMode() === DashboardStudyViewerMode.SurfaceWithProfile ||
        this.studyViewerMode() === DashboardStudyViewerMode.Surface)
    ) {
      // Set the color and opacity for the surfaces in the Papaya viewer.
      this.setColorAndOpacityForSurface(viewRegion);

      // Update the 3D perspective to the view point defined in the view region.
      const viewPoint3D: DashboardViewRegionPosition =
        viewRegion?.world_center_point_3D?.length === 3 ? viewRegion.world_center_point_3D : this.defaultViewRegion3DPoint;
      this.papayaController.move3DPerspectiveToPoint(...viewPoint3D, false);

      // Update the 3D zoom level based on the view region.Add commentMore actions
      const zoom3D: DashboardViewRegionZoom3D = viewRegion.zoom_level_3D || this.defaultViewRegionZoom3D;
      this.papayaController.set3DViewZoom(zoom3D);

      // Update the view inspect point only if the view region was changed.
      // We want to keep the current view position if the user just switched between the view modes.
      if (this.renderedViewRegion()?.id !== viewRegion.id) {
        const viewPointScan: DashboardViewRegionPosition =
          viewRegion?.view_point_position?.length === 3 ? viewRegion.view_point_position : this.defaultViewInspectPoint;
        this.papayaController.moveScanInspectPoint(...viewPointScan, false);
        this.papayaController.move3DViewInspectPoint(...viewPointScan, false);
      }
    }

    // Set the filter values for the view region to show only the relevant scan labels.
    // Update coloring scheme for the labeled scan if it is rendered.
    const renderedScanControllerIds = Array.from(this.scanControlInViewIds);
    if (
      renderedScanControllerIds?.length > 0 &&
      (this.studyViewerMode() === DashboardStudyViewerMode.ScanWithDraw || this.studyViewerMode() === DashboardStudyViewerMode.Scan)
    ) {
      // Set the parameters for the labeled scans in the Papaya viewer.
      this.setParamsForLabeledScans(viewRegion);

      // Update the view inspect point if the view region was changed, or if the scan view was not rendered yet.
      // We want to keep the current view position if the user just switched between the view modes.
      // As we don't provide tools to move the inspect point in the 3D view, the first init of the scan view won't have the correct inspect point set.
      // To fix this, we always set the inspect point if it's the first time we render the scan view (has no cached scan view).
      // If we ever implement the tools to move the inspect point in the 3D view, we can remove this condition and set the inspect point from the 3D view.
      if (this.renderedViewRegion()?.id !== viewRegion.id || !hasCachedScanView) {
        const viewPointScan: DashboardViewRegionPosition =
          viewRegion?.view_point_position?.length === 3 ? viewRegion.view_point_position : this.defaultViewInspectPoint;
        this.papayaController.moveScanInspectPoint(...viewPointScan, false);
      }
    }

    if (redraw) {
      this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.renderingViewer);
      this.papayaController.redrawPapayaViewer();

      // Update the orthogonal view state based on the current viewer mode (only if it was rendered).
      const isOrthogonalView =
        this.studyViewerMode() === DashboardStudyViewerMode.Scan || this.studyViewerMode() === DashboardStudyViewerMode.ScanWithDraw;
      this.isPapayaOrthogonalView.set(isOrthogonalView);
    }

    // Update the rendered view region.
    this.renderedViewRegion.set(viewRegion);
    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.viewerRendered);
  }

  /**
   * Rearranges the slices in the Papaya viewer to match the current viewer mode.
   * @param viewerMode - The current viewer mode to rearrange the slices for.
   */
  public readonly rearrangeSlicesToMatchViewerMode = (viewerMode: DashboardStudyViewerMode): void => {
    switch (viewerMode) {
      // On the surface view modes, we want to show the surface view as the main image.
      case DashboardStudyViewerMode.SurfaceWithProfile:
      case DashboardStudyViewerMode.Surface:
        this.papayaController.currentPapayaInstance.viewer.mainImage = this.papayaController.currentPapayaInstance.viewer.surfaceView;
        this.papayaController.currentPapayaInstance.viewer.lowerImageTop = this.papayaController.currentPapayaInstance.viewer.axialSlice;
        this.papayaController.currentPapayaInstance.viewer.lowerImageBot = this.papayaController.currentPapayaInstance.viewer.coronalSlice;
        this.papayaController.currentPapayaInstance.viewer.lowerImageBot2 =
          this.papayaController.currentPapayaInstance.viewer.sagittalSlice;
        break;

      // On the scan view modes, we want to show the axial slice as the main image.
      case DashboardStudyViewerMode.ScanWithDraw:
      case DashboardStudyViewerMode.Scan:
        this.papayaController.currentPapayaInstance.viewer.mainImage = this.papayaController.currentPapayaInstance.viewer.axialSlice;
        this.papayaController.currentPapayaInstance.viewer.lowerImageTop = this.papayaController.currentPapayaInstance.viewer.coronalSlice;
        this.papayaController.currentPapayaInstance.viewer.lowerImageBot = this.papayaController.currentPapayaInstance.viewer.sagittalSlice;
        this.papayaController.currentPapayaInstance.viewer.lowerImageBot2 = this.papayaController.currentPapayaInstance.viewer.surfaceView;
        break;
    }
  };

  /**
   * Sets the opacity for the focused view region in the Papaya viewer.
   * @param opacity - The opacity value to set for the focused view region (0-1).
   * @param redraw - Whether to redraw the Papaya viewer after setting the opacity.
   */
  public setOpacityForFocusedViewRegion(opacity: number, redraw = true): void {
    const viewRegion = this.renderedViewRegion();

    // Extract the coloring scheme from the related surface regions.
    const coloringSchemes: Partial<Record<DashboardViewRegionMriLabel['scan_source'], Map<number, RGBAColor>>> = {};

    // Iterate through each related region in the view region and create a set of labels to display for each scan source.
    for (const relatedRegion of viewRegion.mri_labels) {
      coloringSchemes[relatedRegion.scan_source] = coloringSchemes[relatedRegion.scan_source] || new Map<number, RGBAColor>();

      for (const scanLabel of relatedRegion.scan_labels) {
        // Add the scan label and its color to the coloring scheme.
        coloringSchemes[relatedRegion.scan_source].set(scanLabel, [...hexColorToRGBColor(relatedRegion.color), opacity]);
      }
    }

    // Set the slice colors, filter values, and max label for each scan controller that is loaded to Papaya viewer.
    for (const scanController of this.papayaController.scanControllerList()) {
      if (scanController.isLoadedToPapaya() && scanController.derivativeType === DashboardSeriesDerivativeType.Labeled) {
        const coloringScheme = coloringSchemes[scanController.scanClass];

        scanController.updateSliceColors(Object.fromEntries(coloringScheme.entries()), false);
      }
    }

    if (redraw) {
      // Redraw the Papaya viewer to apply the changes.
      this.papayaController.redrawPapayaViewer();
    }
  }

  /**
   * Sets the parameters for the labeled scans in the Papaya viewer based on the view region.
   * This includes setting the coloring scheme, filter values, and max drawn label for the labeled scans.
   * @param viewRegion - The view region for which to set the parameters.
   */
  public setParamsForLabeledScans(viewRegion: DashboardViewRegion): void {
    // Extract the coloring scheme from the related surface regions.
    const coloringSchemes: Partial<Record<DashboardViewRegionMriLabel['scan_source'], Map<number, RGBAColor>>> = {};

    // Iterate through each related region in the view region and create a set of labels to display for each scan source.
    for (const relatedRegion of viewRegion.mri_labels) {
      coloringSchemes[relatedRegion.scan_source] = coloringSchemes[relatedRegion.scan_source] || new Map<number, RGBAColor>();

      for (const scanLabel of relatedRegion.scan_labels) {
        // Add the scan label and its color to the coloring scheme.
        coloringSchemes[relatedRegion.scan_source].set(scanLabel, [...hexColorToRGBColor(relatedRegion.color), relatedRegion.opacity]);
      }
    }

    // Set the slice colors, filter values, and max label for each scan controller that is loaded to Papaya viewer.
    for (const scanController of this.papayaController.scanControllerList()) {
      if (scanController.isLoadedToPapaya() && scanController.derivativeType === DashboardSeriesDerivativeType.Labeled) {
        const coloringScheme = coloringSchemes[scanController.scanClass];
        const filterValues = Array.from(coloringScheme.keys());
        const maxLabelToRender = Math.max(...filterValues) + 1; // Add one to include the last label.

        scanController.updateSliceColors(Object.fromEntries(coloringScheme.entries()), false);
        scanController.setFilterValues(filterValues, false);
        scanController.setSliceMaxLabel(maxLabelToRender, false);
      }
    }
  }

  /**
   * Sets the color and opacity for the surfaces in the Papaya viewer based on the view region.
   * @param viewRegion - The view region for which to set the color and opacity.
   */
  public setColorAndOpacityForSurface(viewRegion: DashboardViewRegion): void {
    for (const relatedRegion of viewRegion.mri_labels) {
      const relatedSurfaceController = this.findPapayaSurfaceController(relatedRegion.surface_label);
      if (relatedSurfaceController?.isLoadedToPapaya()) {
        const colorToApply: Partial<OriginalPapayaSurfaces> = {};

        // If the filter color is defined, use it to color only the matching part of the surface.
        if (relatedRegion.filter_color) {
          colorToApply.solidColor = null;
          colorToApply.filterColor = hexColorToPapayaRGBColor(relatedRegion.filter_color);
          colorToApply.mappedColor = hexColorToPapayaRGBColor(relatedRegion.color);
          // If the filter color is not defined, just fill all the surface with the provided color.
        } else {
          colorToApply.solidColor = hexColorToPapayaRGBColor(relatedRegion.color);
          colorToApply.filterColor = null;
          colorToApply.mappedColor = null;
        }

        // Update the surface controller with the new color and opacity.
        relatedSurfaceController.updatePapayaSurfaceParams(
          {
            ...colorToApply,
            alpha: relatedRegion.opacity,
          },
          false
        );
      }
    }
  }

  /**
   * Initializes the Papaya viewer with the necessary controls based on the view region.
   * @param viewRegion - The view region to load.
   */
  public async initialViewRegionLoad(viewRegion: DashboardViewRegion): Promise<void> {
    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.viewerInitialization);

    // Initialize the viewer mode.
    const initialViewerMode = defineInitialViewMode(viewRegion.body_segment);
    this.studyViewerMode.set(initialViewerMode);

    // Define the viewer mode based on the active body segment.
    const initialBaseScanClass = defineInitialBaseScanClass(viewRegion);
    this.selectedBaseScanClass.set(initialBaseScanClass);

    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.loadingFiles);

    // Get the necessary controls for the current mode.
    const controlsNeeded = await this.getNecessaryControlsForCurrentMode(viewRegion);

    // Load default papaya parameters.
    this.papayaController.initializeDefaultPapayaParams();

    // Preload the scan controls if needed.
    if (controlsNeeded.scanControls.length > 0) {
      this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.updatingScanControls);
      this.papayaController.preloadPapayaScanImages();
      this.scanControlInViewIds = new Set(controlsNeeded.scanControls.map(scanControl => scanControl.id));
    }

    // Preload the surface controls if needed.
    if (controlsNeeded.surfaceControls.length > 0) {
      this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.updatingSurfaceControls);
      this.papayaController.preloadPapayaSurfaceImages();
      this.surfaceControlInViewIds = new Set(controlsNeeded.surfaceControls.map(surfaceControl => surfaceControl.id));
    }

    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.renderingViewer);
    // Start the Papaya viewer and update the status variable.
    this.papayaController.startPapaya();

    // Wait for the initial files to be rendered.
    await this.papayaController.initialFilesRendered;

    this.currentLoadingStatus.set(DashboardStudyViewerLoadingState.viewerRendered);

    // Set the rendered view region to the current view region.
    this.isPapayaRendered.set(true);
  }

  /**
   * Returns the necessary controls for the current viewer mode.
   * @param viewRegion - The view region for which to get the necessary controls.
   */
  public async getNecessaryControlsForCurrentMode(viewRegion: DashboardViewRegion): Promise<DashboardViewRegionRequiredControls> {
    const requiredControls: DashboardViewRegionRequiredControls = {
      scanControls: [],
      surfaceControls: [],
    };
    let isScanControlsNeeded = false;
    let isSurfaceControlsNeeded = false;

    // Define the control types needed based on the current viewer mode.
    switch (this.studyViewerMode()) {
      // Only scan controls are needed for the scan-related modes.
      case DashboardStudyViewerMode.Scan:
      case DashboardStudyViewerMode.ScanWithDraw:
        isScanControlsNeeded = true;
        isSurfaceControlsNeeded = false;
        break;

      // For the surface-related modes, only surface controls are needed.
      case DashboardStudyViewerMode.Surface:
      case DashboardStudyViewerMode.SurfaceWithProfile:
        isScanControlsNeeded = false;
        isSurfaceControlsNeeded = true;
        break;

      // For all other modes, neither scan nor surface controls are needed.
    }

    // Add scan controls if they are marked as needed.
    if (isScanControlsNeeded) {
      // Get the base scan class selected by the user.
      requiredControls.scanControls.push(
        await this.getOrCreateScanControl(this.selectedBaseScanClass(), DashboardSeriesDerivativeType.Base)
      );

      // Extract the scan classes needed for the labeled scans from the view region.
      const labeledScanClassesNeeded = new Set<DashboardSeriesScanClass>(viewRegion.mri_labels.map(region => region.scan_source));

      // Add all the labeled scan classes needed for the view region.
      for (const scanSource of Array.from(labeledScanClassesNeeded)) {
        requiredControls.scanControls.push(await this.getOrCreateScanControl(scanSource, DashboardSeriesDerivativeType.Labeled));
      }
    }

    // Add surface controls if they are marked as needed.
    if (isSurfaceControlsNeeded) {
      // Create a set to store unique surface labels to avoid duplicates.
      const storedSurfaceLabels = new Set<DashboardViewRegionMriLabel['surface_label']>();
      const neededSurfaceIdRelations: PapayaSurfaceControllerIdRelation[] = [];

      for (const relatedRegion of viewRegion.mri_labels) {
        // The same region cannot have the same surface label twice; probably an error in the configuration.
        if (storedSurfaceLabels.has(relatedRegion.surface_label)) {
          throw new Error(`Duplicate surface label found: ${relatedRegion.surface_label}`);
        }

        // Add the surface label to the set to avoid duplicates.
        storedSurfaceLabels.add(relatedRegion.surface_label);

        // Store the relation between the surface label and its derivative ID.
        neededSurfaceIdRelations.push({
          id: relatedRegion.surface_label,
          derivativeId: relatedRegion.surface_derivative_id,
        });
      }

      // Get or create the surface controls based on the relations.
      requiredControls.surfaceControls = await this.getOrCreateSurfaceControls(neededSurfaceIdRelations);
    }

    return requiredControls;
  }

  /**
   * Creates or finds multiple Papaya surface controllers based on the provided control ID relations.
   * @param controlIdRelations - An array of relations containing the control IDs and their corresponding derivative IDs.
   */
  public async getOrCreateSurfaceControls(controlIdRelations: PapayaSurfaceControllerIdRelation[]): Promise<PapayaSurfaceController[]> {
    const papayaSurfaceControllersPromises: Promise<PapayaSurfaceController>[] = [];

    // Iterate through each control ID relation and form the related promises.
    for (const controlIdRelation of controlIdRelations) {
      papayaSurfaceControllersPromises.push(this.getOrCreateSurfaceControl(controlIdRelation.id, controlIdRelation.derivativeId));
    }

    // Wait for all surface controllers to be created or found.
    return Promise.all(papayaSurfaceControllersPromises);
  }

  /**
   * Creates a new Papaya surface controller if it doesn't exist, or returns the existing one.
   * @param id - The id of the surface controller to create or find.
   * @param derivativeId - The derivative id of the surface controller to create or find; used to fetch the surface data.
   */
  public async getOrCreateSurfaceControl(
    id: PapayaSurfaceController['id'],
    derivativeId: DashboardViewRegionMriLabel['surface_derivative_id']
  ): Promise<PapayaSurfaceController> {
    // Check if the surface controller already exists.
    const relatedSurfaceController = this.findPapayaSurfaceController(id);

    // If it exists, return it.
    if (relatedSurfaceController) {
      return relatedSurfaceController;
    }

    // If it doesn't exist, create a new one.
    const dashboardPapayaSurfaceFile = await firstValueFrom(this.dashboardStudyLoadFilesService.getMockSurfaceData(id, derivativeId));
    return this.papayaController.addNewSurfaceController(dashboardPapayaSurfaceFile);
  }

  /**
   * Finds the Papaya surface controller by its id.
   * @param id - The ID of the surface controller to find.
   */
  public findPapayaSurfaceController(id: PapayaSurfaceController['id']): PapayaSurfaceController {
    // Check for the surface controller index in the map for faster lookup.
    const papayaSurfaceControllerIndex = this.papayaController.surfaceIdToIndexMap.get(id);

    // If the index is not found, return null without further checks.
    if (typeof papayaSurfaceControllerIndex !== 'number' || papayaSurfaceControllerIndex < 0) {
      return null;
    }

    // Get the surface controller using the index.
    const papayaSurfaceControllers = this.papayaController.surfaceControllerList();
    return papayaSurfaceControllers[papayaSurfaceControllerIndex];
  }

  /**
   * Creates a new Papaya scan controller if it doesn't exist, or returns the existing one.
   * @param scanClass - The scan class of the scan controller to create or find.
   * @param derivativeType - The derivative type of the scan controller to create or find.
   */
  public async getOrCreateScanControl(
    scanClass: DashboardSeriesScanClass,
    derivativeType: DashboardSeriesDerivativeType
  ): Promise<PapayaScanController> {
    const relatedScanController = this.findPapayaScanController(scanClass, derivativeType);

    if (relatedScanController) {
      return relatedScanController;
    }

    const dashboardPapayaImageFile = await firstValueFrom(this.dashboardStudyLoadFilesService.getMockNiftiData(scanClass, derivativeType));
    return this.papayaController.addNewScanController(dashboardPapayaImageFile);
  }

  /**
   * Finds the Papaya scan controller by its derivative type and scan class.
   * @param scanClass - The scan class of the scan controller to find.
   * @param derivativeType - The derivative type of the scan controller to find.
   */
  public findPapayaScanController(
    scanClass: DashboardSeriesScanClass,
    derivativeType: DashboardSeriesDerivativeType
  ): PapayaScanController {
    // We have few scan controllers, and they are harder to be assigned a unique id, so we don't use index map for them.
    const papayaScanControllers = this.papayaController.scanControllerList();

    // Find the scan controller by derivative type and scan class.
    // It's slower than using index map, but we usually need to find only one or two controllers.
    return papayaScanControllers.find(
      scanController => scanController.derivativeType === derivativeType && scanController.scanClass === scanClass
    );
  }

  /**
   * Updates the viewer mode of the study.
   * @param viewerMode - The new viewer mode to set.
   */
  public async updateViewerMode(viewerMode: DashboardStudyViewerMode): Promise<void> {
    // Surface with profile mode does not make sense for the full body segment; ignore the request.
    const selectedBodySegment = this.renderedViewRegion()?.body_segment;
    if (selectedBodySegment === DashboardViewRegionBodySegment.FullBody && viewerMode === DashboardStudyViewerMode.SurfaceWithProfile) {
      return Promise.resolve();
    }

    // Set the new viewer mode.
    this.studyViewerMode.set(viewerMode);

    // Focus the view region.
    await this.focusViewRegion(this.renderedViewRegion(), false);

    // Apply the orthogonal view parameters based on the viewer mode.
    switch (viewerMode) {
      // Three slices for the scan view modes.
      case DashboardStudyViewerMode.Scan:
      case DashboardStudyViewerMode.ScanWithDraw:
        this.papayaController.setOrthogonalViewParam(true);
        break;

      // Single slice view for the surface view modes.
      case DashboardStudyViewerMode.Surface:
      case DashboardStudyViewerMode.SurfaceWithProfile:
        this.papayaController.setOrthogonalViewParam(false);
        break;
    }

    // Reset the surface camera position so the brain image matches the head profile line.
    if (viewerMode === DashboardStudyViewerMode.SurfaceWithProfile) {
      this.papayaController.resetSurfaceCameraPosition(this.renderedViewRegion().body_segment);
    }

    // Redraw the Papaya viewer to apply the changes.
    this.papayaController.redrawPapayaViewer();

    // Update the orthogonal view state based on the current viewer mode.
    const isOrthogonalView = viewerMode === DashboardStudyViewerMode.Scan || viewerMode === DashboardStudyViewerMode.ScanWithDraw;
    this.isPapayaOrthogonalView.set(isOrthogonalView);
  }

  /**
   * Updates the base scan class for the scan view.
   * @param scanClass - The new base scan class to set.
   */
  public async updateBaseScanClass(scanClass: DashboardSeriesScanClass) {
    // Ignore the request if the scan class is already selected.
    const selectedScanClass = this.selectedBaseScanClass();
    if (selectedScanClass === scanClass) {
      return Promise.resolve();
    }

    // Select the new base scan class.
    this.selectedBaseScanClass.set(scanClass);

    // Focus the view region.
    await this.focusViewRegion(this.renderedViewRegion());
  }

  /**
   * Resets the current view to the default view region.
   */
  public resetCurrentView(): void {
    const viewRegion = this.renderedViewRegion();

    // Update the 3D perspective to the view point defined in the view region.
    const viewPoint3D: DashboardViewRegionPosition =
      viewRegion?.world_center_point_3D?.length === 3 ? viewRegion.world_center_point_3D : this.defaultViewRegion3DPoint;
    this.papayaController.move3DPerspectiveToPoint(...viewPoint3D, false);

    // Update the 3D zoom level based on the view region.Add commentMore actions
    const zoom3D: DashboardViewRegionZoom3D = viewRegion.zoom_level_3D || this.defaultViewRegionZoom3D;
    this.papayaController.set3DViewZoom(zoom3D);

    // Update the view inspect point to the default one.
    const inspectPoint: DashboardViewRegionPosition =
      viewRegion?.view_point_position?.length === 3 ? viewRegion.view_point_position : this.defaultViewInspectPoint;
    this.papayaController.moveScanInspectPoint(...inspectPoint, false);
    this.papayaController.move3DViewInspectPoint(...inspectPoint, false);

    this.papayaController.resetSurfaceCameraPosition(this.renderedViewRegion().body_segment);
    this.papayaController.redrawPapayaViewer();
  }

  /**
   * Fits the Papaya viewer to the parent element.
   */
  public fitPapayaToTheParentElement() {
    this.papayaController.fitPapayaToTheParentElement();
  }
}

/**
 * Function to define the initial view mode based on the active body segment.
 * @param activeBodySegment - The active body segment of the view region.
 */
export const defineInitialViewMode = (activeBodySegment: DashboardViewRegionBodySegment): DashboardStudyViewerMode => {
  // Set the viewer mode based on the active body segment:
  switch (activeBodySegment) {
    // For the full body, use Surface mode (as showing profile won't make sense).
    case DashboardViewRegionBodySegment.FullBody:
      return DashboardStudyViewerMode.Surface;

    // For the brain, use SurfaceWithProfile mode.
    case DashboardViewRegionBodySegment.Brain:
      return DashboardStudyViewerMode.SurfaceWithProfile;

    // For some reason the body segment is missing, throw an error.
    default:
      throw new Error('Invalid body segment provided');
  }
};

/**
 * Function to define the initial base scan class based on the view region.
 * @param viewRegion - The view region of the dashboard.
 */
export const defineInitialBaseScanClass = (viewRegion: DashboardViewRegion): DashboardSeriesScanClass => {
  // We always want to show T1 scan as the base scan.
  // But it can be different for different body segments (T1Brain or T1WholeBody).
  switch (viewRegion.body_segment) {
    case DashboardViewRegionBodySegment.Brain:
      return DashboardSeriesScanClass.T1Brain;

    case DashboardViewRegionBodySegment.FullBody:
      return DashboardSeriesScanClass.T1WholeBody;

    // For some reason the body segment is missing, throw an error.
    default:
      throw new Error(`Invalid body segment (${viewRegion.body_segment}) provided for the view region ${viewRegion.name}.`);
  }
};
